import React from 'react';
import { styled, experimental_sx as sx } from '@mui/system';
import * as propz from 'propz';
import { SchoolHouse } from 'models/house';
import { SchoolEvent } from 'models/event';
import { getHousePoints } from 'helpers/house';
import { filterEventsByCurrentSeason } from 'helpers/event';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainerMUI from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRowMUI from '@mui/material/TableRow';
import PageTitle from 'components/PageTitle';
import { HouseTotalsRow } from 'services/awards';

const HOUSES_COLUMNS = [
  { text: 'House',  value: 'house',  align: 'center' },
  { text: 'Points', value: 'points', align: 'center' }
];

interface Props {
  items: SchoolHouse[];
  events?: SchoolEvent[];        
  totals?: HouseTotalsRow[];     
}

type ColumnValue = 'house' | 'points';

const TableContainer = styled(TableContainerMUI)(
  sx({
    border: '1px solid #ccc',
    borderRadius: '5px'
  })
);

const TableRow = styled(TableRowMUI)(
  sx({
    '&:last-child td, &:last-child th': { border: 0 }
  })
);

export default function SchoolHouses(props: Props) {
  const { items, events = [], totals } = props;

  const eventsOfCurrentSeason = filterEventsByCurrentSeason(events);

  const renderHousesTableColumns = () => {
    const columns = HOUSES_COLUMNS.map(column => {
      const { text, value, align } = column;
      return (
        <TableCell key={value} sx={{ textAlign: align, fontWeight: 'bold' }}>
          {text}
        </TableCell>
      );
    });
    return <TableRowMUI>{columns}</TableRowMUI>;
  };

  const housesById = React.useMemo(() => {
    return (items || []).reduce<Record<string, SchoolHouse>>((m, h: any) => {
      const k = String(h?.id ?? h?._id ?? '');
      if (k) m[k] = h;
      return m;
    }, {});
  }, [items]);

  const getHousesTableRows = () => {
    if (totals && totals.length > 0) {
      return totals
        .map(t => {
          const resolved = housesById[String(t.houseId)];
          const houseName =
            propz.get(resolved, ['name'], '') || t.houseName || '';

          return {
            house: houseName,
            points: t.totalPoints
          };
        })
        .sort((a, b) => b.points - a.points);
    }

    return items
      .map(item => {
        const houseName = propz.get(item, ['name'], '');
        return {
          house: houseName,
          points: getHousePoints(eventsOfCurrentSeason, item)
        };
      })
      .sort((a, b) => b.points - a.points);
  };

  const renderHousesTableRows = () => {
    const rows = getHousesTableRows();

    return rows.map(row => {
      const { house, points } = row;

      const rowCellContent = {
        house,
        points
      };

      return (
        <TableRow key={`${house}-${points}`}>
          {HOUSES_COLUMNS.map(column => {
            const { value, align } = column;
            return (
              <TableCell key={value} sx={{ textAlign: align }}>
                {rowCellContent[value as ColumnValue]}
              </TableCell>
            );
          })}
        </TableRow>
      );
    });
  };

  return (
    <>
      <PageTitle text="House Points" />

      <TableContainer>
        <Table>
          <TableHead>{renderHousesTableColumns()}</TableHead>
          <TableBody>{renderHousesTableRows()}</TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
